import React, { useEffect, useState } from "react";
import { api, authHeaders, errMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, Loader2, MessageSquare, CornerDownRight, Flag, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { timeAgo } from "@/lib/timeAgo";

export default function VendorReviews() {
  const [data, setData] = useState({ reviews: [], groups: [], average: null, count: 0, total_reviews: 0 });
  const [loading, setLoading] = useState(true);
  const [replyOpen, setReplyOpen] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [saving, setSaving] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});

  const load = () => api.get("/vendor/reviews", { headers: authHeaders("vendor") })
    .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const openReply = (r) => { setReplyOpen(r.review_id); setReplyText(r.reply || ""); };

  const reportReview = async (r) => {
    const reason = window.prompt("Perché segnali questa recensione? (l'assistenza contatterà il cliente per verificare)");
    if (reason === null) return;
    try {
      await api.post(`/vendor/reviews/${r.review_id}/report`, { reason: reason.trim() }, { headers: authHeaders("vendor") });
      toast.success("Recensione segnalata. L'assistenza verificherà con il cliente.");
      load();
    } catch (e) { toast.error(errMessage(e)); }
  };
  const saveReply = async (r) => {
    setSaving(true);
    try {
      await api.post(`/vendor/reviews/${r.review_id}/reply`, { reply: replyText }, { headers: authHeaders("vendor") });
      toast.success(replyText.trim() ? "Risposta pubblicata" : "Risposta rimossa");
      setReplyOpen(null); load();
    } catch (err) { toast.error(errMessage(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#E63946]" /></div>;

  const disputedMap = {};
  (data.reviews || []).forEach((r) => { disputedMap[r.review_id] = !!r.disputed; });

  const renderReview = (r) => (
    <div key={r.review_id} data-testid={`vendor-review-${r.review_id}`}>
      {r.created_at && <p className="text-[11px] text-slate-400 mb-1" data-testid={`vendor-review-date-${r.review_id}`}>{timeAgo(r.created_at)}</p>}
      {r.comment && <p className="text-sm text-slate-600 leading-relaxed">{r.comment}</p>}

      {r.reply && replyOpen !== r.review_id && (
        <div className="mt-2 ml-1 pl-3 border-l-2 border-[#E63946]/30 bg-slate-50 rounded-r-lg p-2.5" data-testid={`vendor-reply-${r.review_id}`}>
          <p className="text-xs font-bold text-[#E63946] flex items-center gap-1 mb-0.5"><CornerDownRight className="h-3.5 w-3.5" /> La tua risposta</p>
          <p className="text-sm text-slate-600">{r.reply}</p>
        </div>
      )}

      {replyOpen === r.review_id ? (
        <div className="mt-2 space-y-2">
          <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={3} placeholder="Scrivi una risposta pubblica…" className="rounded-xl" data-testid={`reply-input-${r.review_id}`} />
          <div className="flex gap-2">
            <Button onClick={() => saveReply(r)} disabled={saving} data-testid={`reply-save-${r.review_id}`} className="h-10 rounded-xl font-bold flex-1">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (replyText.trim() ? "Pubblica risposta" : "Rimuovi risposta")}
            </Button>
            <Button variant="outline" onClick={() => setReplyOpen(null)} className="h-10 rounded-xl">Annulla</Button>
          </div>
        </div>
      ) : (
        <div className="mt-2 flex items-center gap-4">
          <button onClick={() => openReply(r)} data-testid={`reply-btn-${r.review_id}`} className="text-sm font-bold text-[#E63946]">
            {r.reply ? "Modifica risposta" : "Rispondi"}
          </button>
          {disputedMap[r.review_id] ? (
            <span className="text-xs font-bold text-amber-600 flex items-center gap-1" data-testid={`review-disputed-${r.review_id}`}><Flag className="h-3.5 w-3.5" /> In revisione</span>
          ) : (
            <button onClick={() => reportReview(r)} data-testid={`report-btn-${r.review_id}`} className="text-sm font-semibold text-slate-400 hover:text-amber-600 flex items-center gap-1">
              <Flag className="h-3.5 w-3.5" /> Segnala
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-5" data-testid="vendor-reviews-page">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900">Recensioni</h1>
        <p className="text-slate-500 text-sm">Recensioni verificate dei tuoi clienti. Rispondi per curare la tua reputazione.</p>
      </div>

      <div className="kn-card p-5 flex items-center gap-4" data-testid="vendor-reviews-summary">
        <div className="text-center">
          <p className="text-4xl font-black text-slate-900 leading-none">{data.average ?? "—"}</p>
          <div className="flex items-center gap-0.5 mt-1 justify-center">
            {[...Array(5)].map((_, s) => <Star key={s} className={`h-4 w-4 ${data.average && s < Math.round(data.average) ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}
          </div>
        </div>
        <div className="border-l border-slate-100 pl-4">
          <p className="text-sm text-slate-500">Recensioni totali</p>
          <p className="text-2xl font-extrabold text-slate-900">{data.total_reviews ?? data.count}</p>
          <p className="text-xs text-slate-400 mt-0.5">{data.count} {data.count === 1 ? "cliente" : "clienti"}</p>
        </div>
      </div>

      {(data.groups || []).length === 0 ? (
        <div className="kn-card p-10 text-center text-slate-400">
          <MessageSquare className="h-10 w-10 mx-auto mb-2 text-slate-200" />
          Ancora nessuna recensione. Arriveranno dai clienti che usano i tuoi coupon.
        </div>
      ) : (
        <div className="space-y-3">
          {(data.groups || []).map((g) => {
            const expanded = expandedGroups[g.client_account_id] || g.count === 1;
            const shown = expanded ? g.reviews : g.reviews.slice(0, 1);
            return (
              <div key={g.client_account_id} className="kn-card p-4" data-testid={`vendor-review-group-${g.client_account_id}`}>
                <button
                  type="button"
                  onClick={() => g.count > 1 && setExpandedGroups((p) => ({ ...p, [g.client_account_id]: !p[g.client_account_id] }))}
                  className="w-full flex items-center gap-2.5 text-left"
                  data-testid={`vendor-review-group-toggle-${g.client_account_id}`}
                >
                  <div className="h-9 w-9 rounded-full bg-[#FFE4E6] flex items-center justify-center font-bold text-[#E63946] shrink-0">{(g.client_name || "?")[0]}</div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-800 text-sm truncate">{g.client_name}</p>
                    <div className="flex items-center gap-1 flex-wrap">
                      <div className="flex items-center gap-0.5">
                        {[...Array(5)].map((_, s) => <Star key={s} className={`h-3.5 w-3.5 ${s < Math.round(g.avg) ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}
                      </div>
                      <span className="text-xs font-bold text-amber-600">{g.avg}</span>
                      {g.count > 1 && <span className="text-xs text-slate-400">· {g.count} recensioni</span>}
                      {g.latest_at && <span className="text-xs text-slate-400">· {timeAgo(g.latest_at)}</span>}
                    </div>
                  </div>
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 shrink-0">Verificata</span>
                  {g.count > 1 && (expanded ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />)}
                </button>

                <div className="mt-3 space-y-4">
                  {shown.map((r) => (
                    <div key={r.review_id} className={g.count > 1 ? "pl-11 border-t border-slate-100 pt-3 first:border-t-0 first:pt-0" : ""}>
                      {g.count > 1 && (
                        <div className="flex items-center gap-0.5 mb-1">
                          {[...Array(5)].map((_, s) => <Star key={s} className={`h-3 w-3 ${s < r.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}
                        </div>
                      )}
                      {renderReview(r)}
                    </div>
                  ))}
                  {g.count > 1 && !expanded && (
                    <button type="button" onClick={() => setExpandedGroups((p) => ({ ...p, [g.client_account_id]: true }))} className="pl-11 text-xs font-bold text-[#E63946]" data-testid={`vendor-review-group-more-${g.client_account_id}`}>
                      Leggi e rispondi a tutte le {g.count} recensioni
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
